import type { FormError } from '../../lib/helpers';

import { useStore } from '@tanstack/react-form';
import { useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { batch } from '@tanstack/store';

import { showValidationErrorToast, touchAllFields } from '../../lib/helpers';

import { useFormContext } from '../../form';

interface ValidationToastHandlerProps {
  /**
   * If true, the toast will not be displayed on invalid form submission.
   */
  hideToast?: boolean;
}

const ValidationToastHandler = ({
  hideToast = false,
}: ValidationToastHandlerProps) => {
  const { t } = useTranslation();
  const form = useFormContext();
  const toastRef = useRef<string>(null);
  const submissionAttemptsRef = useRef(0);

  const { isValid, isSubmitting, submissionAttempts, fieldMeta } = useStore(
    form.store,
    (state) => ({
      isValid: state.isValid,
      isSubmitting: state.isSubmitting,
      submissionAttempts: state.submissionAttempts,
      fieldMeta: state.fieldMeta,
    }),
  );

  useEffect(() => {
    /**
     * Tanstack form has onSubmitInvalid function in form.
     * Unfortunately it is called only once, so this is workaround
     * to touch all fields on invalid submit.
     */
    if (submissionAttempts > 0) {
      batch(() => {
        touchAllFields(form);
      });

      /**
       * If the form has field validators, they are called on submit first.
       * If any field has an error, the form is marked as invalid and the whole
       * form validation is not performed.
       */
      form.validate('submit');
    }
  }, [form, submissionAttempts]);

  useEffect(() => {
    if (
      !isValid &&
      submissionAttempts > 0 &&
      submissionAttemptsRef.current !== submissionAttempts
    ) {
      submissionAttemptsRef.current = submissionAttempts;

      if (toastRef.current) toast.remove(toastRef.current);
      if (!hideToast) {
        const formErrors = Object.entries(fieldMeta).reduce(
          (acc, [key, meta]) => {
            acc[key] = (meta as { errors: FormError }).errors;
            return acc;
          },
          {} as Record<string, FormError>,
        );
        toastRef.current = showValidationErrorToast(formErrors);
      }
    }
  }, [fieldMeta, hideToast, isValid, isSubmitting, submissionAttempts, t]);

  return null;
};

export default ValidationToastHandler;
